// Direct imports for optimal tree shaking
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";

import { TraceBehavior } from "./tracebehavior";
import { AnimationData } from "../scenedata/behaviors/animationdata";
import { WrapMode } from "../scenedata/common";
import { Logger } from "../utils/Logger";
import { ValidationError } from "../scenedata/behaviordata";
import { resetTransformToIdentity } from "../utils/TransformUtils";
import type { Playable, PlayableSampled } from "./playable";
import { SceneObject } from "../core/SceneObject";

// TODO: Consider extracting vroot animation ownership into its own behavior
// (shared with AnimationSequence and potentially Rotate).

/**
 * Animation
 *
 * Two independent playback channels:
 *
 * **Embedded clips** (`play()`) — plays clips from the object's GLB model,
 * targeting bones/meshes/materials in the object hierarchy.
 *
 * **External clips** (`playExternal()`) — loads a clip from an external GLB
 * and plays it on the object's animation root (vroot), animating the whole
 * object's position/rotation/scale. Both channels can play simultaneously.
 */
export class Animation extends TraceBehavior<AnimationData> implements Playable, PlayableSampled {
    static readonly behaviorType = "Animation" as const;

    // ── Embedded clip state ──

    /** Loaded animation groups keyed by clip name */
    private loadedGroups: Map<string, AnimationGroup> = new Map();

    /** Currently active embedded animation group (playing or paused) */
    private _activeGroup: AnimationGroup | null = null;

    /** Abort controller for cancelling embedded playback */
    private _abortController: AbortController | null = null;

    /** Time (seconds) at which the active clip naturally completed, or null */
    private _completedAt: number | null = null;

    /**
     * True while the embedded clip is in a Timeline's sampling session
     * (`sampleAt` claimed it). Anything else that drives the clip — play(),
     * seek(), stop(), clearPlayback() — ends the session, and the Timeline
     * stops touching the clip until it claims it again (play from stopped,
     * seek, loop). See `PlayableSampled`.
     */
    private _sampling = false;
    /** Last Timeline time, retained across a re-bind (before speed/wrap mapping). */
    private _sampledTime = 0;

    // ── External clip state (vroot channel) ──

    /** Currently loaded external animation group */
    private _externalGroup: AnimationGroup | null = null;

    /** Abort controller for external playback */
    private _externalAbort: AbortController | null = null;

    /** Playback ID for external channel */
    private _externalPlaybackId: number = 0;


    constructor(sceneObject: SceneObject, data: AnimationData) {
        super(sceneObject, data);
        this.validate(this.getAuthored());
        this._resolved = this._resolveAll();
    }

    /**
     * Validate animation data.
     */
    protected validate(data: AnimationData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validate speed if present
        if (data.speed !== undefined && (typeof data.speed !== 'number' || data.speed <= 0)) {
            errors.push({
                field: 'speed',
                message: 'Speed must be a positive number',
                code: 'INVALID_SPEED'
            });
        }

        // Validate wrapMode if present
        if (data.wrapMode !== undefined) {
            const validModes = Object.values(WrapMode);
            if (!validModes.includes(data.wrapMode)) {
                errors.push({
                    field: 'wrapMode',
                    message: `Wrap mode must be one of: ${validModes.join(', ')}`,
                    code: 'INVALID_WRAP_MODE'
                });
            }
        }

        return errors;
    }

    protected async applyCommit(patch: Partial<AnimationData>, prev: AnimationData): Promise<void> {
        if (this._activeGroup && 'speed' in patch) {
            this._activeGroup.speedRatio = this._resolved.speed ?? 1;
        }

        // Hot-reload external clip when src or clip changes.
        // Also reload when src is the same — the underlying blob may have been
        // updated (e.g. clip editor auto-save overwrites the asset in-place).
        // Use 'in' to detect explicit clears (src set to undefined).
        if ('src' in patch || (patch.clip !== undefined && patch.clip !== prev.clip)) {
            this._cancelExternalPlayback();
            // Dispose old external clips to prevent memory leaks when src changes
            this._removeAllExternalClips();
            if (this._resolved.src) {
                await this._loadAndStoreExternalClip(this._resolved.src);
            }
        }
    }

    /**
     * Load animation resources.
     */
    async load(): Promise<void> {
        // Load external clip if src is configured
        if (this._resolved.src) {
            await this._loadAndStoreExternalClip(this._resolved.src);
        }
    }

    /**
     * Load all animation groups from an external GLB and register them
     * in loadedGroups so they're available via play() / getClipNames().
     */
    private async _loadAndStoreExternalClip(src: string): Promise<void> {
        const groups = await this._loadAllExternalClips(src);
        for (const group of groups) {
            const name = group.name.replace(/^External_/, '') || 'Clip';
            this.loadedGroups.set(name, group);
        }
        if (groups.length > 0) {
            Logger.debug(`[Animation] Loaded ${groups.length} clip(s) from ${src}`);
        }
    }

    protected onAttach(): void {
        // Register embedded clips from the model. Runs after all load() calls
        // complete (Model sets sceneObject.animationClips during its load),
        // so the clips are guaranteed to be available here.
        this._registerEmbeddedClips();
    }

    protected onDetach(): void {
        this.stop();
        this._disposeLoadedGroups();
        this._disposeExternalGroup();
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            if (this._resolved.playAutomatically !== false) {
                this.play().catch(err => {
                    Logger.warn(`[Animation] Auto-play failed for ${this.sceneObject.name}:`, err);
                });
            }
        } else {
            this.stop();
        }
    }

    /**
     * Dispose all loaded animation groups and reset playback state.
     * Shared by onDetach() and hot-reload in commit().
     */
    private _disposeLoadedGroups(): void {
        this._sampling = false;
        this._clearPlayback();
        for (const group of this.loadedGroups.values()) {
            try {
                group.dispose();
            } catch {
                // Ignore disposal errors
            }
        }
        this.loadedGroups.clear();
    }

    // ============================
    // Public Playback API
    // ============================

    /**
     * Play the configured animation.
     *
     * @param options.loop - Override wrap mode to loop
     * @param options.speed - Override playback speed
     * @param options.clip - Specific clip name to play
     * @returns Promise that resolves when animation completes (or is stopped)
     */
    public async play(options?: {
        loop?: boolean;
        speed?: number;
        clip?: string;
    }): Promise<void> {
        const clipName = options?.clip ?? this._resolved.clip;
        // An outside play ends a Timeline's sampling session. It then starts
        // exactly as it would with no Timeline around — from the clip start,
        // not from wherever the Timeline last sampled it.
        const wasSampling = this._sampling;
        this._sampling = false;

        // Resume a genuinely paused animation (user pressed pause mid-playback).
        // Only resume when the abort controller is still active — after stop()
        // or natural completion, _abortController is null/aborted and we must
        // fall through to a full restart so the correct loop flag is applied.
        if (
            this._activeGroup?.isStarted
            && !this._activeGroup.isPlaying
            && this._abortController
            && !this._abortController.signal.aborted
        ) {
            const targetGroup = this._findAnimationGroup(clipName);
            if (!targetGroup || this._activeGroup === targetGroup) {
                if (options?.speed !== undefined) {
                    this._activeGroup.speedRatio = options.speed;
                }
                this._completedAt = null;
                const group = this._activeGroup;
                group.onAnimationGroupEndObservable.addOnce(() => {
                    const fps = group.targetedAnimations[0]?.animation.framePerSecond || 30;
                    this._completedAt = (group.to - group.from) / fps;
                });
                group.play();
                return;
            }
        }

        const speed = options?.speed ?? this._resolved.speed ?? 1;

        // Determine loop mode: explicit option > per-clip override > top-level wrapMode
        let loop: boolean;
        if (options?.loop !== undefined) {
            loop = options.loop;
        } else {
            const clipOverride = clipName
                ? this._resolved.clips?.find(c => c.name === clipName)
                : undefined;
            const effectiveWrap = clipOverride?.wrap ?? this._resolved.wrapMode;
            loop = effectiveWrap === WrapMode.Loop;
        }

        // Find the animation group to play
        const group = this._findAnimationGroup(clipName);

        if (!group) {
            Logger.warn(`[Animation] No animation group found for ${this.sceneObject.name}${clipName ? ` (clip: ${clipName})` : ''}`);
            return;
        }

        // A clip that seek() placed (started, paused, with no playback session
        // yet) plays on from where it stands — the Playable contract: play()
        // resumes, it never rewinds. After stop() the clip stands at its
        // start anyway, so this changes nothing there. Not when a Timeline
        // parked it (see above), and not from the very end of a clip that
        // does not loop: that would finish the instant it started, so a
        // replay starts over.
        const current = group === this._activeGroup && group.isStarted && !group.isPlaying && !wasSampling
            ? group.getCurrentFrame()
            : undefined;
        const startFrame = current !== undefined && (loop || current < group.to) ? current : undefined;

        this._clearPlayback();

        // Create new abort controller
        this._abortController = new AbortController();
        this._activeGroup = group;

        await this._playAnimationGroup(group, loop, speed, this._abortController.signal, startFrame);
    }

    /**
     * Pause all animation playback (Playable interface).
     * Freezes animations at their current frame so they can be resumed
     * with play() or scrubbed with seek().
     */
    public pause(): void {
        if (this._activeGroup?.isPlaying) {
            this._activeGroup.pause();
        }
    }

    /**
     * Stop all animation playback (both embedded and external channels).
     * Rewinds to frame 0 but keeps _activeGroup so getPlaybackState()
     * reports {time: 0, playing: false} for observers.
     */
    public stop(): void {
        this._sampling = false;
        this._rewind();
        this._cancelExternalPlayback();
    }

    /**
     * Clear all playback state, returning the behavior to a "no clip active" state.
     * After calling this, getPlaybackState() returns null.
     * Used by the clip editor to ensure a clean slate when starting a new clip session.
     */
    public clearPlayback(): void {
        this._sampling = false;
        this._clearPlayback();
        this._cancelExternalPlayback();
    }

    /**
     * Seek the active (or default) animation to a specific time in seconds.
     * Converts seconds to frame number using the animation's FPS.
     *
     * If the animation hasn't been started yet, starts it in a paused state
     * so the frame is visually applied.
     *
     * @param time - Time in seconds
     */
    public seek(time: number): void {
        // An outside seek ends a Timeline's sampling session (the Timeline
        // itself places sampled clips with sampleAt, never seek).
        this._sampling = false;
        // If no group is active yet, start the default clip paused so the seek visually applies
        if (!this._activeGroup) {
            const group = this._findAnimationGroup(this._resolved.clip);
            if (!group) return;
            group.start(false, this._resolved.speed ?? 1);
            group.pause();
            this._activeGroup = group;
        }

        const group = this._activeGroup;
        if (!group.isStarted) {
            group.start(false, this._resolved.speed ?? 1);
            group.pause();
        }
        this._completedAt = null;
        const fps = group.targetedAnimations[0]?.animation?.framePerSecond ?? 30;
        const frame = time * fps;
        const clampedFrame = Math.max(group.from, Math.min(frame, group.to));
        group.goToFrame(clampedFrame);
    }

    /**
     * PlayableSampled: claim the configured clip for the calling Timeline and
     * put it at Timeline time `time`, paused. A Timeline samples keyframe
     * clips every frame rather than letting them run on Babylon's clock (see
     * `PlayableSampled`).
     *
     * Claiming ends whatever session was running — an outside play() of this
     * clip or another settles as stopped, as a Timeline's seek + play did
     * before sampling existed.
     *
     * Timeline time IS the clip's own glTF time (RECORDINGS_SPEC §4.2):
     * `frame = t × speed × fps`, from frame 0 — NOT from `group.from`. The
     * lead-in trim `SceneObject` applies to loaded clips (`from` past a
     * constant hold at 0) is a play() convenience; a Timeline-sampled clip
     * never sees it (DESIGN_PRINCIPLES §10.10), so a clip whose first keys
     * happen to be equal still plays on its authored time base. Wrapped over
     * [0, to] when the clip's effective wrap mode is `Loop`, clamped otherwise.
     */
    public sampleAt(time: number): void {
        const group = this._findAnimationGroup(this._resolved.clip) ?? this._activeGroup;
        if (!group) return;
        this._sampledTime = time;
        if (!this._sampling) {
            if (this._activeGroup && this._activeGroup !== group) {
                this._clearPlayback();
            } else if (this._abortController) {
                this._abortController.abort();
                this._abortController = null;
            }
            this._sampling = true;
        }
        this._activeGroup = group;
        if (!group.isStarted) {
            group.start(false, this._resolved.speed ?? 1, 0, group.to);
        }
        if (group.isPlaying) group.pause();
        this._completedAt = null;

        const fps = group.targetedAnimations[0]?.animation?.framePerSecond ?? 30;
        const end = group.to;
        let frame = Math.max(0, time) * (this._resolved.speed ?? 1) * fps;
        if (this._effectiveLoop(this._getClipName(group)) && end > 0 && frame > end) {
            frame %= end;
        }
        group.goToFrame(Math.min(frame, end));
    }

    /** PlayableSampled: whether the session sampleAt() claimed is still current. */
    public isSampling(): boolean {
        return this._sampling;
    }

    /** Whether `clipName` loops: its per-clip wrap override, else the top-level wrapMode. */
    private _effectiveLoop(clipName: string | undefined): boolean {
        const clipOverride = clipName
            ? this._resolved.clips?.find(c => c.name === clipName)
            : undefined;
        return (clipOverride?.wrap ?? this._resolved.wrapMode) === WrapMode.Loop;
    }

    /**
     * Check if animation is currently playing (not paused or stopped).
     * Covers both the embedded clip and an externally-loaded one
     * (trace/playAnimationExternal drives a separate group) — this is the
     * PlayableState the graph's trace/isMediaPlaying reads, and "is this
     * object animating?" should be true for either source.
     */
    public isPlaying(): boolean {
        return this._activeGroup?.isPlaying === true || this.isExternalPlaying();
    }

    /**
     * Set playback speed in real-time.
     * Affects currently playing animation.
     *
     * @param speed - Speed multiplier (1.0 = normal)
     */
    public setSpeed(speed: number): void {
        if (this._activeGroup) {
            this._activeGroup.speedRatio = speed;
        }
    }

    /**
     * Get the current playback state for the active animation.
     * Returns null when no clip is active (never started or fully cleared).
     */
    public getPlaybackState(): { time: number; duration: number; clipName: string; playing: boolean } | null {
        const group = this._activeGroup
            ?? (this._externalGroup?.isStarted ? this._externalGroup : null);
        if (!group) return null;

        const fps = group.targetedAnimations[0]?.animation.framePerSecond || 30;
        const duration = (group.to - group.from) / fps;

        // After natural completion Babylon resets getCurrentFrame() to 0,
        // so use the captured completion time instead.
        const time = this._completedAt ?? group.getCurrentFrame() / fps;

        return {
            time,
            duration,
            clipName: this._getClipName(group),
            playing: group.isPlaying,
        };
    }

    /** @deprecated Use getPlaybackState() */
    public getPlaybackTime() { return this.getPlaybackState(); }

    /**
     * Get available animation clip names (without the scoped prefix).
     */
    public getClipNames(): string[] {
        return [...this.loadedGroups.keys()];
    }

    /**
     * Get clip names the SceneObject provides (embedded): its model's own
     * clips plus clips bound from `TRACE_animation_binding` sources — as
     * opposed to external clips loaded from a separate GLB via `src`. The
     * SceneObject owns these groups, so this behavior never disposes them.
     */
    public getEmbeddedClipNames(): string[] {
        return [...this.sceneObject.animationClips.keys()];
    }

    /**
     * Get clip names loaded from the external `src` GLB (custom clips),
     * i.e. clips in loadedGroups that are NOT embedded in the model.
     */
    public getExternalClipNames(): string[] {
        const embedded = this.sceneObject.animationClips;
        return [...this.loadedGroups.keys()].filter(name => !embedded.has(name));
    }

    /**
     * Get a loaded AnimationGroup by clip name.
     * Returns null if the clip hasn't been loaded yet.
     */
    public getLoadedGroup(clipName: string): AnimationGroup | null {
        return this.loadedGroups.get(clipName) ?? null;
    }

    /**
     * Remove an external clip by name, disposing its animation group.
     * No-op if the clip is embedded (own or bound) or doesn't exist.
     */
    public removeExternalClip(clipName: string): void {
        const embedded = this.sceneObject.animationClips;
        if (embedded.has(clipName)) return; // Don't remove embedded clips

        const group = this.loadedGroups.get(clipName);
        if (!group) return;

        // Stop if this clip is currently playing
        if (this._activeGroup === group) {
            this._clearPlayback();
        }
        if (this._externalGroup === group) {
            this._cancelExternalPlayback();
        }

        group.dispose();
        this.loadedGroups.delete(clipName);
    }

    /**
     * Dispose and remove all external clips from loadedGroups.
     * Embedded clips (from the model) are kept.
     */
    private _removeAllExternalClips(): void {
        for (const name of this.getExternalClipNames()) {
            const group = this.loadedGroups.get(name);
            if (group) {
                if (this._activeGroup === group) {
                    this._clearPlayback();
                }
                group.dispose();
                this.loadedGroups.delete(name);
            }
        }
    }

    // ============================
    // Internal Methods
    // ============================

    /**
     * Rewind to frame 0 but keep _activeGroup so getPlaybackState() reports
     * {time: 0, playing: false}. Used by stop().
     */
    private _rewind(): void {
        this._stopActiveGroup(false);
    }

    /**
     * Fully clear embedded playback state. Used by play() before starting
     * a new clip and by _disposeLoadedGroups().
     */
    private _clearPlayback(): void {
        this._stopActiveGroup(true);
    }

    /**
     * Abort playback, rewind to frame 0, and optionally release the active group reference.
     * @param clearRef - If true, nulls _activeGroup (full clear); if false, keeps it (rewind/stop).
     */
    private _stopActiveGroup(clearRef: boolean): void {
        if (this._abortController) {
            this._abortController.abort();
            this._abortController = null;
        }

        if (this._activeGroup) {
            if (!this._activeGroup.isStarted) {
                this._activeGroup.start(false, 1);
            }
            this._activeGroup.goToFrame(this._activeGroup.from);
            if (this._activeGroup.isPlaying) {
                this._activeGroup.pause();
            }
            if (clearRef) {
                this._activeGroup = null;
            }
        }

        this._completedAt = null;
        this._resetSkeleton();
    }

    /** Return skeleton to rest pose so bones from the previous clip don't stay at stale positions. */
    private _resetSkeleton(): void {
        const meshes = this.sceneObject.transform.getChildMeshes(false);
        const skeleton = meshes.find(m => m.skeleton)?.skeleton;
        if (skeleton) {
            skeleton.returnToRest();
        }
    }

    /**
     * The object's bound clips were replaced (a re-bind that did not rebuild
     * the object, e.g. a `RestRelativeBinding` commit). Called before the
     * previous groups are disposed: a clip of theirs that is active is
     * stopped, their entries are dropped, and the object's current clips are
     * registered. A Timeline sampling claim resumes at the same time on the
     * replacement. Clips loaded from this behavior's own `src` are kept.
     * @internal (`SceneObject._setBoundAnimationClips`)
     */
    public _onBoundClipsReplaced(previous: ReadonlyMap<string, AnimationGroup>): void {
        const current = new Set(this.sceneObject.animationClips.values());
        const stale = new Set([...previous.values()].filter(group => !current.has(group)));
        const resumeSampling = this._sampling && !!this._activeGroup && stale.has(this._activeGroup);
        if (this._activeGroup && stale.has(this._activeGroup)) {
            this._sampling = false;
            this._clearPlayback();
        }
        for (const [name, group] of [...this.loadedGroups]) {
            if (stale.has(group)) this.loadedGroups.delete(name);
        }
        this._registerEmbeddedClips();
        // Rebuilding keys is not an outside playback command: keep the
        // Timeline's claim and pose, including while paused at its preview.
        if (resumeSampling) this.sampleAt(this._sampledTime);
    }

    /** Copy the SceneObject's model animation clips into our loadedGroups map. */
    private _registerEmbeddedClips(): void {
        for (const [clipName, group] of this.sceneObject.animationClips) {
            this.loadedGroups.set(clipName, group);
        }

        Logger.debug(`[Animation] Found ${this.loadedGroups.size} embedded clips for ${this.sceneObject.name}`);
    }

    /**
     * Reverse-lookup the clean clip name for a group from loadedGroups.
     * Falls back to stripping the "External_" prefix from the raw Babylon
     * group name — this handles groups from playExternal() which are not
     * stored in loadedGroups but are named "External_<clipName>".
     */
    private _getClipName(group: AnimationGroup): string {
        for (const [name, g] of this.loadedGroups) {
            if (g === group) return name;
        }
        return group.name.replace(/^External_/, '');
    }

    private _findAnimationGroup(clipName?: string): AnimationGroup | null {
        if (!this.loadedGroups.size) return null;

        if (clipName) {
            return this.loadedGroups.get(clipName) ?? null;
        }

        // Return first clip
        return this.loadedGroups.values().next().value ?? null;
    }

    private async _playAnimationGroup(
        group: AnimationGroup,
        loop: boolean,
        speed: number,
        signal: AbortSignal,
        startFrame?: number,
    ): Promise<void> {
        return new Promise<void>(resolve => {
            if (signal.aborted) {
                resolve();
                return;
            }

            // Position (the start, unless resuming a seeked clip) and configure
            group.goToFrame(startFrame ?? group.from);
            group.setWeightForAllAnimatables(1);
            group.speedRatio = Math.max(speed, 0.01);

            let isDone = false;
            let abortHandler: (() => void) | null = null;
            let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

            const cleanup = () => {
                if (abortHandler) {
                    signal.removeEventListener('abort', abortHandler);
                }
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle);
                }
            };

            const settle = () => {
                if (!isDone) {
                    isDone = true;
                    cleanup();
                    resolve();
                }
            };

            // Set up completion listener (only for non-looping)
            if (!loop) {
                const onEndObserver = group.onAnimationGroupEndObservable.addOnce(() => {
                    const fps = group.targetedAnimations[0]?.animation.framePerSecond || 30;
                    this._completedAt = (group.to - group.from) / fps;
                    settle();
                });

                // Safety timeout (30 seconds adjusted for speed)
                timeoutHandle = setTimeout(() => {
                    if (!isDone && group.isPlaying) {
                        group.stop();
                        group.onAnimationGroupEndObservable.remove(onEndObserver);
                        settle();
                    }
                }, 30000 / Math.max(speed, 0.01));
            }

            // Set up abort handler
            abortHandler = () => {
                if (!isDone) {
                    if (group.isPlaying) {
                        group.stop();
                    }
                    settle();
                }
            };
            signal.addEventListener('abort', abortHandler);

            // Start playback.
            // Use play() instead of start() — start() is a no-op when the group
            // is already started (e.g. after stop→rewind leaves it paused).
            // play() handles both started and not-started groups correctly and
            // propagates the loop flag to all animatables.
            group.play(loop);
        });
    }

    // ============================
    // External Clip Playback (vroot channel)
    // ============================

    /**
     * Load an external GLB and play a clip on the object's animation root.
     *
     * This is independent from embedded clip playback — both can run simultaneously.
     * Each call replaces the previous external clip (one at a time on the vroot channel).
     *
     * @param src - URL/path to the external GLB file
     * @param options.clip - Specific clip name within the GLB (defaults to first)
     * @param options.loop - Whether to loop the animation
     * @param options.speed - Playback speed multiplier
     * @param options.onStarted - Called once the clip is loaded and driving the
     *   object (playback begins). Not called when the load is abandoned by a
     *   newer playExternal()/stop().
     * @returns Promise that resolves when animation completes or is stopped
     */
    public async playExternal(src: string, options?: {
        clip?: string;
        loop?: boolean;
        speed?: number;
        onStarted?: () => void;
    }): Promise<void> {
        const speed = options?.speed ?? 1;
        const loop = options?.loop ?? false;

        this._cancelExternalPlayback();

        // Claim this playback slot before the async load so a subsequent call
        // can detect (and cancel) an in-flight load.
        const currentId = ++this._externalPlaybackId;
        this._externalAbort = new AbortController();
        const signal = this._externalAbort.signal;

        // Load the external animation
        const group = await this._loadExternalClip(src, options?.clip);

        // Abandon if another playExternal() arrived or stop() was called during load
        if (!group || this._externalPlaybackId !== currentId || signal.aborted) {
            group?.dispose();
            return;
        }

        // Defensive — _cancelExternalPlayback already disposed, but guard against future changes
        this._disposeExternalGroup();
        this._externalGroup = group;

        options?.onStarted?.();

        try {
            await this._playAnimationGroup(group, loop, speed, signal);
        } finally {
            if (this._externalPlaybackId === currentId && !signal.aborted && !loop) {
                // Reset the animated node to identity on natural completion.
                // Skip if aborted — _cancelExternalPlayback already resets.
                this._resetExternalTarget();
            }
        }
    }

    /**
     * Check if an external clip is currently playing.
     */
    public isExternalPlaying(): boolean {
        return this._externalGroup?.isPlaying === true;
    }

    // ── External clip internals ──

    private _cancelExternalPlayback(): void {
        if (this._externalAbort) {
            this._externalAbort.abort();
            this._externalAbort = null;
        }

        if (this._externalGroup?.isPlaying) {
            this._externalGroup.stop();
        }
        this._disposeExternalGroup();
        this._resetExternalTarget();
    }

    /** Reset the node that external clips animate back to identity. */
    private _resetExternalTarget(): void {
        if (this._resolved.target === '@self') {
            // @self target: no reset — the SceneObject's transform is its authored position
            return;
        }
        // @vroot (default): reset animation root to identity
        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            resetTransformToIdentity(animRoot, '[Animation]');
        }
    }

    private _disposeExternalGroup(): void {
        if (this._externalGroup) {
            try { this._externalGroup.dispose(); } catch { /* ignore */ }
            this._externalGroup = null;
        }
    }

    /** Load ALL animation groups from an external GLB, retarget each. */
    private async _loadAllExternalClips(src: string): Promise<AnimationGroup[]> {
        // Through the ResourceManager, as AnimationSequence does: it resolves a
        // folder document's relative `./assets/...` against the document's URL,
        // and shares the parsed GLB. We only read its groups (cloning each
        // animation), so release our reference once, whatever happens.
        let acquired = false;
        try {
            const container = await this.resources.getOrLoadContainer(src, this.scene);
            acquired = true;

            if (!container.animationGroups.length) {
                Logger.warn(`[Animation] No animation groups in external source ${src}`);
                return [];
            }

            const targetNode = this._resolved.target === '@self'
                ? this.sceneObject.transform
                : this.sceneObject.getOrCreateAnimationRoot();

            const results: AnimationGroup[] = [];
            for (const sourceGroup of container.animationGroups) {
                const cleanName = sourceGroup.name.replace(/^External_/, '');
                const retargeted = new AnimationGroup(`External_${cleanName}`, this.scene);
                for (const ta of sourceGroup.targetedAnimations) {
                    retargeted.addTargetedAnimation(ta.animation.clone(), targetNode);
                }
                results.push(retargeted);
            }

            return results;
        } catch (e) {
            Logger.error(`[Animation] Failed to load external clips from ${src}: ${String(e)}`);
            return [];
        } finally {
            if (acquired) this.resources.releaseContainer(src);
        }
    }

    /** Load a single clip from an external GLB by name (used by playExternal). */
    private async _loadExternalClip(src: string, clipName?: string): Promise<AnimationGroup | null> {
        const groups = await this._loadAllExternalClips(src);
        if (groups.length === 0) return null;

        let match: AnimationGroup | undefined;
        if (clipName) {
            match = groups.find(g => g.name.replace(/^External_/, '') === clipName);
            if (!match) {
                Logger.warn(`[Animation] Clip "${clipName}" not found in ${src}. Available: ${
                    groups.map(g => g.name.replace(/^External_/, '')).join(', ')}`);
                for (const g of groups) g.dispose();
                return null;
            }
        } else {
            match = groups[0];
        }

        // Dispose unused groups
        for (const g of groups) {
            if (g !== match) g.dispose();
        }
        return match;
    }

}
